import { useCallback, useEffect, useRef, useState } from 'react';
import type { AssistantMessage } from '@brandschutz/shared';
import { streamChat } from './api';

/**
 * React-Hook für den Companion-Bot-Chat.
 *
 * State:
 *  - messages       — finalisierte Nachrichten (user + assistant).
 *  - partialReply   — aktuell streamender Assistant-Reply.
 *  - isStreaming    — true zwischen send() und onDone/onError.
 *  - error          — letzter Fehler (z. B. KI-Service nicht konfiguriert).
 *
 * Methoden:
 *  - send(prompt, projectId?)
 *  - clear()
 *  - stop()
 */

export interface UseAssistantState {
  messages: AssistantMessage[];
  partialReply: string;
  isStreaming: boolean;
  error: string | null;
  send: (prompt: string, projectId?: number) => void;
  clear: () => void;
  stop: () => void;
}

export function useAssistant(): UseAssistantState {
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [partialReply, setPartialReply] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ref auf die aktuelle Stream-Abortion, damit stop() und unmount
  // sicher den Stream beenden.
  const abortRef = useRef<AbortController | null>(null);
  // Ref auf den akkumulierten Reply, damit Closures nicht stale Werte lesen.
  const partialRef = useRef('');

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
  }, []);

  const clear = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setMessages([]);
    setPartialReply('');
    setIsStreaming(false);
    setError(null);
    partialRef.current = '';
  }, []);

  const send = useCallback(
    (prompt: string, projectId?: number) => {
      const trimmed = prompt.trim();
      if (trimmed.length === 0) return;
      if (isStreaming) return;

      const userMessage: AssistantMessage = { role: 'user', content: trimmed };
      // Wir bauen die History für den Stream-Call mit der neuen User-Message.
      const history = [...messages, userMessage];

      setMessages(history);
      setPartialReply('');
      setError(null);
      setIsStreaming(true);
      partialRef.current = '';

      abortRef.current = streamChat(history, projectId, {
        onDelta: (delta) => {
          partialRef.current += delta;
          setPartialReply(partialRef.current);
        },
        onDone: () => {
          const finalReply = partialRef.current;
          if (finalReply.trim().length > 0) {
            setMessages((prev) => [...prev, { role: 'assistant', content: finalReply }]);
          }
          setPartialReply('');
          setIsStreaming(false);
          partialRef.current = '';
          abortRef.current = null;
        },
        onError: (message) => {
          setError(message);
          setPartialReply('');
          setIsStreaming(false);
          partialRef.current = '';
          abortRef.current = null;
        },
      });
    },
    [isStreaming, messages],
  );

  return { messages, partialReply, isStreaming, error, send, clear, stop };
}
